import {
  ArrowDownward,
  ArrowUpward,
} from '@mui/icons-material';

import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import PropTypes from 'prop-types';
import Spinners from '../../components/Common/Spinner';
import './Table.scss';

const CustomTable = ({
  data,
  columns,
  pagination,
  setPagination,
  manualPagination,
  rowCount,
  pageCount,
  sorting,
  setSorting,
  isLoading,
}) => {
  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    manualPagination,
    pageCount,
    rowCount,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const toggleSort = (column) => {
    setSorting((prev) => {
      const current = prev?.[0];
      if (!current || current.id !== column.id) {
        return [{ id: column.id, desc: false }];
      }
      if (current.id === column.id && current.desc === false) {
        return [{ id: column.id, desc: true }];
      }
      if (current.id === column.id && current.desc === true) {
        return [{ id: column.id, desc:false }];
      }
      return [];
    });
  };


  return (
    <div className="table-wrapper">
      <Table>
        {/* HEADER */}
        <TableHead>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => {
                const col = header.column;

                return (
                  <TableCell
                    key={header.id}
                    onClick={
                      col.columnDef.enableSorting
                        ? () => toggleSort(col)
                        : undefined
                    }
                    className={
                      col.columnDef.enableSorting ? "sortable-header" : ""
                    }
                  >
                    <div className="header-cell">
                      {flexRender(col.columnDef.header, header.getContext())}

                      {col.columnDef.enableSorting && (
                        sorting[0]?.id === col.id ? (
                          sorting[0]?.desc ? (
                            <ArrowDownward sx={{ color: "#fff", ml:1 }} />
                          ) : (
                            <ArrowUpward sx={{ color: "#fff", ml:1 }} />
                          )
                        ) : (
                          null
                        )
                      )}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>

        {/* BODY */}
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <div className="flex justify-center p-10 w-full">
                  <Spinners />
                </div>
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <h3>No data available</h3>
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        {/* FOOTER */}
        {!isLoading && (
          <>
            <TableFooter>
              <tr>
                <td>
                  <span>Count: {table.getRowCount()}</span>
                </td>
                <td colSpan={columns.length} style={{ textAlign: "right", paddingTop: 12 }}>
                  <Button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} sx={{ mr: 1 }}>Prev</Button>
                  <span style={{ marginRight: 8 }}>Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
                  <Button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} sx={{ ml: 1 }}>Next</Button>

                  <span style={{ marginLeft: 16, marginRight: 8 }}>Rows per page:</span>
                  <FormControl size="small" sx={{ width: 90 }}>
                    <Select
                      value={pagination.pageSize}
                      onChange={(e) => setPagination(p => ({ ...p, pageSize: Number(e.target.value), pageIndex: 0 }))}
                    >
                      {[5, 10, 20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                  </FormControl>
                </td>
              </tr>
            </TableFooter>
          </>
        )}
      </Table>
    </div>
  );
};

CustomTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  setPagination: PropTypes.func.isRequired,
  manualPagination: PropTypes.bool,
  pageCount: PropTypes.number,
  sorting: PropTypes.object,
  setSorting: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default CustomTable;
